import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Activity, Loader2, RefreshCw, AlertTriangle, CheckCircle2, Coins } from "lucide-react";

/**
 * AdminCashouts — operational dashboard for the crypto cashout pipeline.
 *
 * Surfaces:
 *   - Worker enabled / disabled
 *   - Stripe Bridge LIVE vs MOCKED
 *   - Sanctions screener mode (Chainalysis / TRM / mock)
 *   - Queue counters by status
 *   - Last 20 cashout attempts
 *
 * Read-only — uses GET /api/admin/payouts/crypto-bridge-status.
 * Admin actions (approve/reject) already live in /admin?tab=payouts (older UI).
 */
export default function AdminCashouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payouts/crypto-bridge-status");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load cashout status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="admin-cashouts">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Coins size={14} className="text-secondary" strokeWidth={1.75} />
            <span className="label-mystic text-secondary">Crypto Cashouts</span>
          </div>
          <h2 className="font-serif text-3xl mt-1">USDC Bridge Status</h2>
          <p className="text-sm text-foreground/65 mt-1 max-w-xl">
            Live status of the crypto cashout worker, Stripe Bridge connectivity,
            sanctions screening, and the queue of pending payouts.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-secondary/30 text-xs uppercase tracking-[0.18em] text-foreground/75 hover:bg-secondary/[0.08] disabled:opacity-50"
          data-testid="admin-cashouts-refresh"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Refresh
        </button>
      </div>

      {!data && loading && (
        <div className="text-xs text-foreground/55">Loading…</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatusTile
              label="Worker"
              value={data.workerEnabled ? "ENABLED" : "DISABLED"}
              tone={data.workerEnabled ? "ok" : "warn"}
              testid="admin-cashouts-worker"
            />
            <StatusTile
              label="Stripe Bridge"
              value={data.bridgeLive ? "LIVE" : "MOCKED"}
              tone={data.bridgeLive ? "ok" : "warn"}
              testid="admin-cashouts-bridge"
            />
            <StatusTile
              label="Sanctions screener"
              value={data.sanctionsScreener.toUpperCase()}
              tone={data.sanctionsScreener === "mock" ? "warn" : "ok"}
              testid="admin-cashouts-screener"
            />
          </div>

          <div className="parchment p-4 text-xs text-foreground/75" data-testid="admin-cashouts-bridge-note">
            <Activity size={11} className="inline mr-1.5 text-secondary" strokeWidth={1.75} />
            {data.bridgeNote}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-cashouts-counts">
            {["pending", "screening", "sending", "paid", "failed"].map((st) => (
              <CountTile key={st} status={st} n={data.counts[st] ?? 0} />
            ))}
          </div>

          <div className="parchment p-4 space-y-2">
            <div className="label-mystic text-secondary/80 text-[10px] mb-2">Recent attempts</div>
            {(!data.recent || data.recent.length === 0) && (
              <div className="text-xs text-foreground/55">No cashout attempts yet.</div>
            )}
            {(data.recent || []).map((w) => (
              <div
                key={w.id}
                className="grid grid-cols-12 gap-2 text-[11px] border-t border-secondary/10 pt-2 first:border-0 first:pt-0"
                data-testid={`admin-cashouts-row-${w.id}`}
              >
                <div className="col-span-2 font-mono text-foreground/55 truncate">{w.id.slice(0, 8)}…</div>
                <div className="col-span-2">
                  <StatusBadge status={w.status} />
                </div>
                <div className="col-span-2 text-foreground/75">
                  {w.amountCaul?.toLocaleString?.() ?? w.amountCaul} CAUL
                  {w.usdAmount ? <span className="text-foreground/40 ml-1">· ${Number(w.usdAmount).toFixed(2)}</span> : null}
                </div>
                <div className="col-span-3 font-mono text-foreground/65 truncate" title={w.methodHandle}>
                  {w.chain ? `${w.chain}:` : ""}{w.methodHandle?.slice(0, 14)}…
                </div>
                <div className="col-span-3 text-foreground/55 text-right">
                  {w.txHash ? (
                    <span className="font-mono truncate inline-block max-w-full" title={w.txHash}>
                      tx {w.txHash.slice(0, 8)}…{w.txHash.slice(-6)}
                    </span>
                  ) : (
                    <span className="text-foreground/40">no tx yet</span>
                  )}
                </div>
                {w.failureReason && (
                  <div className="col-span-12 text-destructive">{w.failureReason}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusTile({ label, value, tone, testid }) {
  const tones = {
    ok: "border-secondary/40 text-secondary bg-secondary/[0.06]",
    warn: "border-amber-500/40 text-amber-200 bg-amber-500/[0.06]",
    bad: "border-destructive/40 text-destructive bg-destructive/[0.06]",
  };
  return (
    <div className={`rounded-sm border p-4 ${tones[tone] || tones.ok}`} data-testid={testid}>
      <div className="label-mystic text-[10px] opacity-80">{label}</div>
      <div className="font-serif text-2xl mt-1 flex items-center gap-2">
        {tone === "ok" ? <CheckCircle2 size={16} strokeWidth={1.75} /> : <AlertTriangle size={16} strokeWidth={1.75} />}
        {value}
      </div>
    </div>
  );
}

function CountTile({ status, n }) {
  const labels = {
    pending: "Pending",
    screening: "Screening",
    sending: "Sending",
    paid: "Paid",
    failed: "Failed",
  };
  return (
    <div className="parchment p-3" data-testid={`admin-cashouts-count-${status}`}>
      <div className="label-mystic text-secondary/70 text-[10px]">{labels[status] || status}</div>
      <div className="font-serif text-3xl text-secondary mt-1">{n}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: "bg-foreground/10 text-foreground/70",
    screening: "bg-amber-500/20 text-amber-200",
    sending: "bg-secondary/20 text-secondary",
    paid: "bg-emerald-500/20 text-emerald-200",
    failed: "bg-destructive/20 text-destructive",
  };
  return (
    <span className={`inline-block text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${colors[status] || ""}`}>
      {status}
    </span>
  );
}
